import { Card } from "shared/components/Card";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail01Icon,
  SmartPhone01Icon,
  TelephoneIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { formatAddress } from "shared/utils";
import { formatToCurrency } from "shared/utils/currency";
import { getAvatarInitials } from "shared/utils/avatar-initials";
import { Row } from "views/applications-id-report/components/Row";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Borrower } from "../types";
import {
  formatDate,
  formatLabel,
  formatMonthYear,
  formatPhoneOrDash,
  formatTenure
} from "../utils";

const ContactPill = ({
  icon,
  value
}: {
  icon: React.ReactNode;
  value?: string;
}) => {
  if (!value || value === "—") return null;
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-content-400">
      {icon}
      {value}
    </span>
  );
};

const EntryHeading = ({
  title,
  isCurrent
}: {
  title: string;
  isCurrent: boolean;
}) => (
  <div className="flex items-start justify-between gap-2">
    <p className="text-sm font-bold text-content-1000">{title}</p>
    <Show if={isCurrent}>
      <div className="shrink-0">
        <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
          Current
        </Badge>
      </div>
    </Show>
  </div>
);

export const BorrowerCard = ({ borrower }: { borrower: Borrower }) => {
  const fullName = `${borrower.firstName} ${borrower.lastName}`.trim();
  const activeIncome = borrower.otherIncome.filter(
    (income) => income.monthlyAmount.cents > 0
  );

  return (
    <Card className="p-5">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-alpha-40 pb-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-20 text-sm font-bold text-primary-600">
          {getAvatarInitials(fullName)}
        </div>
        <div className="flex-1">
          <p className="text-base font-bold text-content-1000">{fullName}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <ContactPill
              icon={<HugeiconsIcon icon={Mail01Icon} size={13} />}
              value={borrower.email}
            />
            <ContactPill
              icon={<HugeiconsIcon icon={SmartPhone01Icon} size={13} />}
              value={formatPhoneOrDash(borrower.cellPhone)}
            />
            <ContactPill
              icon={<HugeiconsIcon icon={TelephoneIcon} size={13} />}
              value={formatPhoneOrDash(borrower.homePhone)}
            />
          </div>
        </div>
        <Badge
          variant={
            borrower.isPrimary ? BadgeVariant.PRIMARY : BadgeVariant.BASE
          }
          size={BadgeSize.SM}
        >
          {borrower.isPrimary ? "Primary applicant" : "Co-borrower"}
        </Badge>
      </div>

      {/* Demographics */}
      <ContentTitle title="Borrower details">
        <div className="space-y-2">
          <Row title="Date of birth" value={formatDate(borrower.dateOfBirth)} />
          <Row title="SSN" value={borrower.ssn} />
          <Row
            title="Marital status"
            value={formatLabel(borrower.maritalStatus)}
          />
          <Row
            title="Employment status"
            value={formatLabel(borrower.employmentStatus)}
          />
          <Row
            title="Ownership status"
            value={formatLabel(borrower.propertyOwnershipStatus)}
          />
          <Row title="Ethnicity" value={formatLabel(borrower.ethnicity)} />
          <Row title="Race" value={formatLabel(borrower.race)} />
        </div>
      </ContentTitle>

      {/* Address history */}
      <Show if={borrower.addresses.length > 0}>
        <ContentTitle title="Address history">
          <div className="space-y-4">
            {borrower.addresses.map((address, idx) => (
              <div key={`${address.street}-${idx}`} className="space-y-2">
                <EntryHeading
                  isCurrent={address.isCurrent}
                  title={formatAddress({
                    street: address.street,
                    unit: address.address_two,
                    city: address.city,
                    state: address.state,
                    zip: address.zip
                  })}
                />
                <Row
                  title="Time at address"
                  value={formatTenure(address.residentMonths)}
                />
                <Row title="Ownership" value={formatLabel(address.ownership)} />
                <Row title="Usage" value={formatLabel(address.usageType)} />
              </div>
            ))}
          </div>
        </ContentTitle>
      </Show>

      {/* Employment history */}
      <Show if={borrower.employment.length > 0}>
        <ContentTitle title="Employment history">
          <div className="space-y-4">
            {borrower.employment.map((job, idx) => (
              <div key={`${job.employerName}-${idx}`} className="space-y-2">
                <EntryHeading
                  isCurrent={job.isCurrent}
                  title={job.employerName}
                />
                <Row title="Position" value={job.position} />
                <Row title="Type" value={formatLabel(job.employmentType)} />
                <Row
                  title="Location"
                  value={formatAddress({
                    city: job.city,
                    state: job.state,
                    zip: job.zip
                  })}
                />
                <Row
                  title="Period"
                  value={`${formatMonthYear(job.dateHired)} – ${
                    job.isCurrent
                      ? "Present"
                      : formatMonthYear(job.dateTerminated)
                  }`}
                />
                <Row
                  title="Monthly base pay"
                  value={formatToCurrency({
                    cents: Math.round(Number(job.basePay || 0) * 100),
                    currency: "USD"
                  })}
                />
                <Show if={job.isSelfEmployed}>
                  <Row
                    title="Ownership"
                    value={`${job.selfEmployedOwnershipPct}%`}
                  />
                </Show>
              </div>
            ))}
          </div>
        </ContentTitle>
      </Show>

      {/* Assets */}
      <Show if={borrower.assets.length > 0}>
        <ContentTitle title="Assets">
          <div className="space-y-2">
            {borrower.assets.map((asset, idx) => (
              <Row
                key={`${asset.name}-${idx}`}
                fullWidthRow
                title={`${asset.institutionName} · ${formatLabel(asset.accountType)}`}
                value={formatToCurrency(asset.balance)}
              />
            ))}
          </div>
        </ContentTitle>
      </Show>

      {/* Other income */}
      <Show if={activeIncome.length > 0}>
        <ContentTitle title="Other income">
          <div className="space-y-2">
            {activeIncome.map((income, idx) => (
              <Row
                key={`${income.description}-${idx}`}
                fullWidthRow
                title={formatLabel(income.description)}
                value={`${formatToCurrency(income.monthlyAmount)} / mo`}
              />
            ))}
          </div>
        </ContentTitle>
      </Show>
    </Card>
  );
};
